import React, { useMemo } from "react";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./treatmentProtocolDetails.module.css";
import { TreatmentProtocol } from "../../../services/types/treatmentProtocol.types";
import { DeviceConfigSections, Item } from "./deviceConfigDetails";
import { frequencyTypeLabelKey } from "../../../pages/treatmentProtocols/treatmentProtocolSummary";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../../services/selectors/utils.selectors";
import {
  useGetAllMetaTablesQuery,
  useGetQuestionnaireCompaniesHeadersQuery,
} from "../../../services/api/utils.api";

interface TreatmentProtocolDetailsProps {
  protocol: TreatmentProtocol;
  onClose: () => void;
}

/**
 * Read-only view of a protocol — the same fields the editor collects, rendered as text rather than
 * disabled inputs, since a greyed-out input still reads as something that ought to be editable.
 */
function TreatmentProtocolDetails({ protocol, onClose }: TreatmentProtocolDetailsProps) {
  const admin = useAppSelector(adminSelector);
  const isRegular = protocol.kind === "regular";

  // Only the regular kind has ids that need resolving to names.
  useGetAllMetaTablesQuery(admin.companyId, { skip: !isRegular });
  const meta = useAppSelector(appointmentsProgramsDataSelector);
  const { data: questionnaireHeaders } = useGetQuestionnaireCompaniesHeadersQuery(
    { companyId: admin.companyId },
    { skip: !isRegular },
  );

  const questionnaireName = (headerId: number) =>
    questionnaireHeaders?.find((h) => h.id === headerId)?.desc ?? `#${headerId}`;

  const namedById = (list: { Id: number; Name: string }[] | undefined, id?: number | null) =>
    (id ? list?.find((item) => item.Id === id)?.Name : undefined) ?? "—";

  const sequenceLabel = useMemo(
    () =>
      meta.AppointmentsSequence.find((s) => s.Id === protocol.appointmentsSequenceId)?.label ?? "—",
    [meta.AppointmentsSequence, protocol.appointmentsSequenceId],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>{protocol.name}</h2>
        <span className={styles.kindBadge}>
          {t(isRegular ? "TREATMENT_PROTOCOL_REGULAR" : "TREATMENT_PROTOCOL_DEVICE")}
        </span>
      </div>

      {isRegular ? (
        <>
          <section className={styles.section}>
            <div className={styles.sectionTitle}>{t("SERIES_SETTINGS")}</div>
            <div className={styles.grid}>
              <Item label={t("APPOINTMENT_FREQUENCY")} value={sequenceLabel} />
              <Item label={t("SESSION_COUNT")} value={protocol.countOfAppointments ?? "—"} />
              <Item
                label={t("APPOINTMENT_DURATION")}
                value={
                  protocol.appointmentsDurationsOpenMinutes
                    ? `${protocol.appointmentsDurationsOpenMinutes} ${t("MINUTES_SHORT")}`
                    : "—"
                }
              />
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionTitle}>{t("TREATMENT_PLAN_DETAILS")}</div>
            <div className={styles.grid}>
              <Item
                label={t("TREATMENT_TYPE")}
                value={namedById(meta.TreatmentTypes, protocol.treatmentTypesId)}
              />
              <Item
                label={t("FUNDING_SOURCE")}
                value={namedById(meta.FundingSources, protocol.fundingSourcesId)}
              />
              <Item
                label={t("TREATMENT_ROOM")}
                value={
                  (protocol.treatmentRoomId
                    ? meta.TreatmentRoomsList.find((r) => r.Id === protocol.treatmentRoomId)?.Name
                    : undefined) ?? "—"
                }
              />
              <Item label={t("MEDIA")} value={namedById(meta.Medias, protocol.mediasId)} />
              <Item label={t("SUPPORT_ROLE")} value={namedById(meta.Roles, protocol.roleId)} />
              <Item label={t("GENDER")} value={namedById(meta.Genders, protocol.genderId)} />
              <Item label={t("SECTOR")} value={namedById(meta.Migzars, protocol.migzarId)} />
            </div>
          </section>

          {protocol.questionnaires.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionTitle}>{t("PROTOCOL_QUESTIONNAIRES")}</div>
              <div className={styles.rowList}>
                {protocol.questionnaires.map((questionnaire) => (
                  <div className={styles.row} key={questionnaire.questionnaireCompaniesHeadersId}>
                    <span className={styles.rowKey}>
                      {questionnaireName(questionnaire.questionnaireCompaniesHeadersId)}
                    </span>
                    <span className={styles.rowValue}>
                      {t(frequencyTypeLabelKey(questionnaire.frequencyType))}
                      {questionnaire.everyNSessions ? ` · ${questionnaire.everyNSessions}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      ) : (
        <DeviceConfigSections config={protocol.deviceConfig} />
      )}

      {protocol.comments && (
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("COMMENTS")}</div>
          <p className={styles.notes}>{protocol.comments}</p>
        </section>
      )}

      <div className={styles.footer}>
        <button type="button" className={styles.closeBtn} onClick={onClose}>
          {t("APPOINTMENTS_CLOSE")}
        </button>
      </div>
    </div>
  );
}

export default TreatmentProtocolDetails;
